import { normalizeKhutbah } from './khutbah';
import { z } from 'zod';
import type { Design, Masjid, PrayerSettings, SalahMode, Widget } from './types';
import { defaultSalahMode } from './salah';
import { ASPECTS } from './types';
import { WIDGETS, defaultText, newId, MIN_TEXT, MAX_TEXT } from './widgets';
import { MAX_BORDER } from './look';
import { clampRect, DEFAULT_GAP, DEFAULT_MARGIN, regrid, MAX_COLS, MAX_MARGIN, MAX_ROWS, MIN_COLS, MIN_MARGIN, MIN_ROWS, minSize } from './grid';
import { blankDesign } from './templates';

/**
 * Validation for everything that crosses a trust boundary (the database, a file, another tab). Screens only ever
 * render normalised designs, so damaged or hand-edited data can't break a display.
 */

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const hhmmS = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

const textSchema = z.object({
  font: z.string().max(60),
  weight: z.union([z.literal(400), z.literal(500), z.literal(600), z.literal(700), z.literal(800)]),
  size: z.number().min(8).max(400),
  align: z.enum(['left', 'center', 'right']),
  case: z.enum(['normal', 'upper', 'lower']),
  scale: z.boolean(),
});

const widgetSchema = z.object({
  id: z.string().min(1).max(64),
  type: z.enum(Object.keys(WIDGETS) as [keyof typeof WIDGETS, ...Array<keyof typeof WIDGETS>]),
  x: z.number().int(), y: z.number().int(), w: z.number().int(), h: z.number().int(),
  name: z.string().max(60).optional(),
  hidden: z.boolean().optional(),
  locked: z.boolean().optional(),
  style: z.enum(['glass', '3d', 'flat', 'legacy', 'none']).catch('glass'),
  accent: hex.catch('#0A84FF'),
  fill: hex.nullable().optional().catch(null),
  textColor: hex.nullable().optional().catch(null),
  border: z.object({ width: z.number().min(0).max(MAX_BORDER), color: hex }).nullable().optional().catch(null),
  opacity: z.number().min(0).max(100).catch(100),
  fit: z.number().min(10).max(100).nullable().optional().catch(null),
  radius: z.number().min(0).max(64).nullable().optional().catch(null),
  text: textSchema.optional(),
  props: z.record(z.string(), z.unknown()).catch({}),
});

const designSchema = z.object({
  schema: z.literal(1).catch(1),
  aspect: z.enum(['16:9', '9:16', '21:9', '4:3']).catch('16:9'),
  cols: z.number().int().min(MIN_COLS).max(MAX_COLS),
  rows: z.number().int().min(MIN_ROWS).max(MAX_ROWS),
  gap: z.number().min(0).max(0.5).catch(DEFAULT_GAP),
  margin: z.number().min(MIN_MARGIN).max(MAX_MARGIN).catch(DEFAULT_MARGIN).default(DEFAULT_MARGIN),
  background: z.object({
    kind: z.enum(['solid', 'gradient', 'image']), color: hex, color2: hex.optional(), image: z.string().max(2048).optional(),
    pattern: z.object({
      id: z.enum(['khatam', 'hexstar', 'zellige', 'circles', 'lattice', 'moroccan', 'rosette', 'kufic', 'arches']),
      color: hex, opacity: z.number().min(0).max(100), coverage: z.enum(['full', 'border', 'corners']), size: z.enum(['small', 'medium', 'large']),
    }).nullable().optional().catch(null),
  })
    .catch({ kind: 'gradient', color: '#0B1626', color2: '#02060D' }),
  widgets: z.array(z.unknown()).max(80),
});

/** Strings in props are capped so one huge paste can't slow a screen down. */
function capProps(v: unknown, depth = 0): unknown {
  if (typeof v === 'string') return v.slice(0, 4000);
  if (Array.isArray(v)) return depth > 3 ? [] : v.slice(0, 60).map((x) => capProps(x, depth + 1));
  if (v && typeof v === 'object') {
    if (depth > 3) return {};
    const o: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v).slice(0, 40)) o[k] = capProps(x, depth + 1);
    return o;
  }
  return typeof v === 'number' && !Number.isFinite(v) ? 0 : v;
}

const isSafeUrl = (u: unknown) => typeof u === 'string' && (/^https:\/\//i.test(u) || /^data:image\/(png|jpe?g|webp|gif);base64,/i.test(u) || /^blob:/i.test(u) || /^\/media\//.test(u));

export function normalizeWidget(raw: unknown, cols: number, rows: number): Widget | null {
  const p = widgetSchema.safeParse(raw);
  if (!p.success) return null;
  const w = p.data;
  const def = WIDGETS[w.type];
  const props = { ...(def.defaults() as Record<string, unknown>), ...(capProps(w.props) as Record<string, unknown>) };
  if (w.type === 'posters' && Array.isArray(props.items)) props.items = (props.items as Array<{ src?: unknown }>).filter((it) => isSafeUrl(it?.src));
  const text = w.text ? { ...defaultText(w.type), ...textSchema.parse(w.text) } : defaultText(w.type);
  text.size = Math.max(MIN_TEXT, Math.min(MAX_TEXT, Math.round(text.size)));
  const r = clampRect(w, cols, rows, minSize(w, cols, rows));
  const out: Widget = { ...w, ...r, text, props, ...(w.name ? { name: w.name } : {}) };
  if (out.fit == null) delete out.fit; else out.fit = Math.round(out.fit);
  if (out.radius == null) delete out.radius; else out.radius = Math.round(out.radius);
  if (!out.fill) delete out.fill;
  if (!out.textColor) delete out.textColor;
  if (!out.border || out.border.width <= 0) delete out.border; else out.border = { width: Math.round(out.border.width), color: out.border.color };
  return out;
}

export function normalizeDesign(raw: unknown): Design {
  const p = designSchema.safeParse(raw);
  if (!p.success) return blankDesign();
  const d = p.data;
  const seen = new Set<string>();
  const widgets: Widget[] = [];
  for (const rw of d.widgets) {
    const w = normalizeWidget(rw, d.cols, d.rows);
    if (!w) continue;
    if (seen.has(w.id)) w.id = newId();
    seen.add(w.id);
    widgets.push(w);
  }
  const bg = { ...d.background };
  if (bg.kind === 'image' && !isSafeUrl(bg.image)) { bg.kind = 'solid'; delete bg.image; }
  // Every screen shape has one fixed grid; older designs are mapped onto it in proportion.
  const a = ASPECTS[d.aspect];
  const fixed = d.cols === a.cols && d.rows === a.rows ? widgets : regrid(widgets, d, a);
  return { schema: 1, aspect: d.aspect, cols: a.cols, rows: a.rows, gap: d.gap, margin: d.margin, background: bg, widgets: fixed };
}

export function defaultPrayerSettings(): PrayerSettings {
  return {
    method: 'MoonsightingCommittee', madhab: 'Hanafi', highLatitudeRule: 'SeventhOfTheNight', adjustments: {},
    jamaah: {
      fajr: { mode: 'offset', minutes: 20, roundTo: 5 },
      dhuhr: { mode: 'fixed', time: '13:30', minutes: 15, roundTo: 5 },
      asr: { mode: 'offset', minutes: 15, roundTo: 5 },
      maghrib: { mode: 'offset', minutes: 5 },
      isha: { mode: 'offset', minutes: 15, roundTo: 5 },
    },
    jumuah: [{ label: 'First Jumu‘ah', khutbah: '13:15', jamaah: '13:30' }],
    eid: [],
    hijriAdjust: 0,
    salahMode: defaultSalahMode(),
  };
}

const blankRule = z.object({ on: z.boolean(), delaySec: z.number().int().min(15).max(1200), minutes: z.number().int().min(1).max(120) });
const jamaahRule = z.object({ mode: z.enum(['offset', 'fixed', 'none']), minutes: z.number().min(0).max(180).optional(), time: hhmmS.optional(), roundTo: z.number().min(0).max(30).optional() });
const prayerSchema = z.object({
  method: z.enum(['MoonsightingCommittee', 'MuslimWorldLeague', 'Egyptian', 'Karachi', 'UmmAlQura', 'Dubai', 'Qatar', 'Kuwait', 'Singapore', 'Turkey', 'Tehran', 'NorthAmerica']),
  madhab: z.enum(['Shafi', 'Hanafi']),
  highLatitudeRule: z.enum(['MiddleOfTheNight', 'SeventhOfTheNight', 'TwilightAngle']),
  adjustments: z.record(z.string(), z.number().min(-60).max(60)),
  jamaah: z.object({ fajr: jamaahRule, dhuhr: jamaahRule, asr: jamaahRule, maghrib: jamaahRule, isha: jamaahRule }),
  jumuah: z.array(z.object({ label: z.string().max(40), khutbah: hhmmS, jamaah: hhmmS })).max(4),
  eid: z.array(z.object({ id: z.string(), name: z.string().max(40), date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), takbeer: hhmmS.optional(), jamaats: z.array(hhmmS).max(6) })).max(10),
  hijriAdjust: z.number().int().min(-2).max(2),
  salahMode: z.object({
    enabled: z.boolean(),
    clock: z.enum(['top-left', 'top-center', 'top-right']),
    h12: z.boolean(),
    prayers: z.object({ fajr: blankRule, dhuhr: blankRule, asr: blankRule, maghrib: blankRule, isha: blankRule, jumuah: blankRule, eid: blankRule }),
  }).catch(defaultSalahMode()),
});

/** The masjid's colours: up to 8 different #RRGGBB values, in the order they were chosen. */
export const MAX_MASJID_COLOURS = 8;
export function masjidColours(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const c of raw) {
    const hex = typeof c === 'string' && /^#?[0-9a-f]{6}$/i.test(c.trim()) ? `#${c.trim().replace('#', '').toUpperCase()}` : null;
    if (hex && !out.includes(hex)) out.push(hex);
    if (out.length === MAX_MASJID_COLOURS) break;
  }
  return out;
}

export function normalizeMasjid(raw: Partial<Masjid> & { id: string }): Masjid {
  const tzOk = (() => { try { new Intl.DateTimeFormat('en', { timeZone: raw.timezone }); return !!raw.timezone; } catch { return false; } })();
  // Older settings may lack Salah mode, or some of its prayers: fill those in from the defaults.
  const sm0 = defaultSalahMode(), smRaw = (raw.prayer?.salahMode ?? {}) as Partial<SalahMode>;
  const salahMode = { ...sm0, ...smRaw, prayers: { ...sm0.prayers, ...(smRaw.prayers ?? {}) } };
  const prayer = prayerSchema.safeParse({ ...defaultPrayerSettings(), ...(raw.prayer ?? {}), salahMode });
  return {
    id: raw.id,
    name: String(raw.name ?? 'My Masjid').slice(0, 80),
    timezone: tzOk ? raw.timezone! : 'Europe/London',
    latitude: Number.isFinite(raw.latitude) ? Math.max(-90, Math.min(90, raw.latitude!)) : 53.7957,
    longitude: Number.isFinite(raw.longitude) ? Math.max(-180, Math.min(180, raw.longitude!)) : -1.7594,
    city: raw.city?.slice(0, 60),
    prayer: prayer.success ? (prayer.data as PrayerSettings) : defaultPrayerSettings(),
    plan: raw.plan === 'basic' || raw.plan === 'pro' ? raw.plan : 'free',
    logo: isSafeUrl(raw.logo) ? raw.logo! : null,
    colors: masjidColours(raw.colors),
    exitPinHash: typeof raw.exitPinHash === 'string' && /^[0-9a-f]{64}$/.test(raw.exitPinHash) ? raw.exitPinHash : null,
    khutbahAddon: raw.khutbahAddon === true,
    khutbah: normalizeKhutbah(raw.khutbah),
  };
}

export const aspectSize = (d: Pick<Design, 'aspect'>) => ASPECTS[d.aspect];
